<template>
    <div class="modal fade" id="purchaseModal" tabindex="-1" aria-hidden="true" ref="modalEl">
        <div class="modal-dialog modal-dialog-centered  w-[600px] ">
            <div class="modal-content" v-if="props.plan">
                <Form @submit="onUpdate" class="">

                    <div class="modal-header">
                        <h1 class="modal-title fs-5">{{ preview ? 'Purchases (admin preview)' : t('purchase.title') }}</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="t('common.close')"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="preview" class="preview-note text-sm">
                            <v-icon icon="mdi-eye-outline" size="16"></v-icon>
                            Preview only. Everything works as it does for a customer, coupons included, but pressing the button buys nothing.
                            <div class="flex items-center gap-2 mt-2">
                                <span class="text-xs whitespace-nowrap">Test as customer (optional)</span>
                                <input v-model="asEmail" placeholder="customer@email, for customer-only coupons and their balance" autocomplete="off"
                                    class="form-control shadow-none rounded-0 text-black" style="height: 32px; font-size: 12px;" />
                            </div>
                        </div>

                        <div class="flex space-x-2">
                            <div class="text-sm w-1/2 pt-4 space-y-2">
                                <ProxyLoginFields v-model="login" :min-length="4" />
                                <div v-if="couponsAllowed" class="d-flex align-items-center gap-3">
                                    <span>{{ t('purchase.couponCode') }}</span>
                                </div>
                                <div v-else class="text-xs" style="color: var(--ui-dim);">
                                    <v-icon icon="mdi-ticket-percent-outline" size="14"></v-icon> {{ t('purchase.noCoupons') }}
                                </div>
                                <div v-if="isCoupon && couponsAllowed" class="relative">
                                    <input v-model="code" :placeholder="t('purchase.couponPlaceholder')" autocomplete="off"
                                        class="form-control shadow-none rounded-0 text-black pr-9"
                                        :class="quote?.coupon ? (quote.coupon.valid ? 'is-valid' : 'is-invalid') : ''" />
                                    <span class="coupon-state">
                                        <v-icon v-if="quoting" icon="mdi-loading" size="16" class="spin"></v-icon>
                                        <v-icon v-else-if="quote?.coupon?.valid" icon="mdi-check-circle" size="16" style="color: var(--ui-ok);"></v-icon>
                                        <button v-else-if="code" type="button" class="coupon-clear" :title="t('purchase.clearCoupon')" :aria-label="t('purchase.clearCoupon')" @click="code = ''">
                                            <v-icon icon="mdi-close-circle" size="16" :style="quote?.coupon && !quote.coupon.valid ? 'color: var(--ui-dan);' : 'color: var(--ui-dim);'"></v-icon>
                                        </button>
                                    </span>
                                </div>
                                <div v-if="isCoupon && couponsAllowed && quote?.coupon" :key="couponKey" class="text-xs" :style="quote.coupon.valid ? 'color: var(--ui-ok)' : 'color: var(--ui-dan)'">
                                    {{ quote.coupon.valid ? t('purchase.couponApplied', { label: quote.coupon.label }) : quote.coupon.reason }}
                                </div>
                            </div>
                            <div class="w-1/2 pt-4 space-y-2">
                                <span class="text-sm">{{ t('purchase.quantity') }}</span>
                                <input type="number" min="1" v-model="portQuantity" @input="clampQuantity" @change="clampQuantity"
                                    class="form-control shadow-none rounded-0 text-black" />
                                <div v-if="stockHint" :key="'h' + stockHint" class="text-xs" style="color: #f0997b;">{{ stockHint }}</div>
                                <div v-else-if="maxStock > 0" :key="'a' + maxStock" class="text-xs" style="color: var(--ui-dim);">{{ t('purchase.availableHere', { n: maxStock }) }}</div>
                                <!-- live price breakdown -->
                                <div v-if="props.plan?.max_speed_mbps" class="speed-note text-sm">
                                    <v-icon icon="mdi-speedometer" size="16"></v-icon> {{ t('purchase.speedNote', { n: props.plan.max_speed_mbps }) }}
                                </div>
                                <!-- :key redraws the box whenever a number changes: a browser translation ("Translate this page")
                                     replaces our text with its own copy, which would otherwise keep showing the old numbers -->
                                <div class="price-box text-sm" :key="priceKey">
                                    <div class="row-line"><span>${{ money(props.plan.price) }} × {{ portQuantity || 0 }}</span><span>${{ money(subtotal) }}</span></div>
                                    <div v-if="isCoupon && quote?.coupon?.valid" class="row-line" style="color: var(--ui-ok);"><span>{{ t('purchase.coupon', { code: quote.coupon.code }) }}</span><span>− ${{ money(quote.discount) }}</span></div>
                                    <div class="row-line total"><span>{{ t('purchase.total') }}</span><span>${{ money(total) }}</span></div>
                                    <div v-if="isCoupon && quote && !quote.error" class="row-line text-xs" :style="quote.enough ? 'color: var(--ui-dim)' : 'color: var(--ui-dan)'">
                                        <span>{{ t('purchase.balanceAfter') }}</span><span>${{ money(quote.balance_after) }}<template v-if="!quote.enough">{{ t('purchase.notEnough', { balance: money(quote.balance) }) }}</template></span>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                    <div class="modal-footer">
                        <!-- <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button> -->
                        <button type="submit" :key="purchaseLabel" :disabled="buttonDisableFlag || purchaseBlocked" class="btn btn-primary">{{ purchaseLabel }}</button>
                    </div>
                </Form>

            </div>
        </div>
    </div>
</template>
<script setup>
import { t } from '@/i18n';
import { defineProps, ref, watchEffect, computed, watch, onMounted, onBeforeUnmount } from "vue"
import { useRouter } from 'vue-router';
import { Form } from 'vee-validate';
import { useAccountStore, usePackageStore, useAlertStore } from "@/stores"
import ProxyLoginFields from '@/components/Customer/ProxyLoginFields.vue';
import { loginFrom, loginValid, loginToAuth } from '@/helpers/proxyLogin';

const name = process.env.VUE_APP_VUE_NAME

const accountStore = useAccountStore()
const packageStore = usePackageStore()
const props = defineProps({
    plan: {
        type: Object, // Assuming user object has a name property
        required: true
    },
    selectedDetails: {
        type: Object, // Assuming user object has a name property
        required: true
    },
    selectedServerType: {
        type: String, // Assuming user object has a name property
        required: true
    },
    openVPN: {
        type: Boolean,
        required: true
    },
    maxStock: {            // ports available for the chosen city; the quantity cannot go above it
        type: Number,
        default: 0
    },
    preview: {             // admin Proxy Stock page: the whole flow runs, but nothing is purchased
        type: Boolean,
        default: false
    }
    
});
const router = useRouter();

const portQuantity = ref(1)
const modalEl = ref(null)
const stockHint = ref('')
let stockHintTimer = null
// never above the stock of the chosen city; a short hint explains why the number snapped back.
// (no max attribute on the input: with one, the browser arrows stop silently and no hint could be shown)
const clampQuantity = () => {
    const max = Number(props.maxStock) || 0
    const n = Math.floor(Number(portQuantity.value))
    if (max > 0 && n > max) {
        portQuantity.value = max
        stockHint.value = t('purchase.onlyAvailable', { n: max })
        clearTimeout(stockHintTimer); stockHintTimer = setTimeout(() => { stockHint.value = '' }, 4000)
    } else if (Number.isFinite(n) && n < 1) {
        portQuantity.value = 1
    }
}
// every opening starts from 1 with no coupon, whatever was typed for a previous location
const resetForOpen = () => { portQuantity.value = 1; code.value = ''; stockHint.value = ''; login.value = loginFrom() }
const buttonDisableFlag = ref(false)
// the proxy login (username + password, or IP whitelist); a fresh random login for every opening
const login = ref(loginFrom());
const code = ref('');
const isCoupon = ref(true);

// Live quote: price, coupon check and balance, re-fetched (debounced) whenever the plan, the
// quantity or the coupon code changes. Nothing is consumed until Purchase is pressed.
const quote = ref(null);
const quoting = ref(false);
let quoteTimer = null, quoteSeq = 0;
const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const subtotal = computed(() => (Number(props.plan?.price) || 0) * (Number(portQuantity.value) || 0));
const total = computed(() => (isCoupon.value && quote.value && !quote.value.error) ? Number(quote.value.total) : subtotal.value);
// keys for the live parts (price box, coupon line): a new key redraws them, so they stay right under a browser translation
const priceKey = computed(() => [props.plan?.price, portQuantity.value, subtotal.value, total.value, isCoupon.value, quote.value?.discount, quote.value?.balance_after, quote.value?.enough, quote.value?.coupon?.valid].join('|'));
const couponKey = computed(() => [quote.value?.coupon?.code, quote.value?.coupon?.valid, quote.value?.coupon?.reason].join('|'));
// Must be a real boolean: an empty string here would be treated as :disabled="true" by Vue.
const couponTyped = computed(() => String(code.value || '').trim().length > 0);
const couponInvalid = computed(() => couponTyped.value && !!quote.value?.coupon && !quote.value.coupon.valid);
const asEmail = ref('');   // preview only: price it as this customer
// the server's "Disable coupons" tick box: no coupon field at all, and nothing is sent
const couponsAllowed = computed(() => !(quote.value && quote.value.coupons_allowed === false));
watch(couponsAllowed, (ok) => { if (!ok && code.value) code.value = ''; });
// in preview the admin's own (zero) balance must not block; with "test as customer" the customer's balance applies
const balanceShort = computed(() => Boolean(quote.value && !quote.value.error && !quote.value.enough && !(props.preview && !String(asEmail.value || '').trim())));
const purchaseBlocked = computed(() => Boolean(isCoupon.value && quote.value && !quote.value.error && (balanceShort.value || couponInvalid.value)));
const purchaseLabel = computed(() => {
    if (isCoupon.value && balanceShort.value) return t('purchase.btnNotEnough');
    if (isCoupon.value && couponInvalid.value) return t('purchase.btnCheckCoupon');
    return props.preview ? 'Preview purchase for $' + money(total.value) : t('purchase.btnPurchaseFor', { price: money(total.value) });
});
const refreshQuote = () => {
    clearTimeout(quoteTimer);
    if (!props.plan?.uuid || !isCoupon.value || !(Number(portQuantity.value) > 0)) { quote.value = null; return; }
    quoting.value = true;
    const seq = ++quoteSeq;
    quoteTimer = setTimeout(async () => {
        const q = await packageStore.quote(props.plan.uuid, Number(portQuantity.value), String(code.value || '').trim(), props.preview ? String(asEmail.value || '').trim() : '');
        if (seq !== quoteSeq) return;   // a newer request is in flight
        quote.value = q; quoting.value = false;
    }, 350);
};
watch([() => props.plan?.uuid, portQuantity, code, isCoupon, asEmail], refreshQuote, { immediate: true });

const cityStockKey = (city) => {
    const isIpv6 = city?.ip_version === 'IPv6' || city?.is_ipv6 === true || city?.is_ipv6 === 'true';
    return `${String(city?.city || '').trim().toLowerCase()}::${isIpv6 ? 'IPv6' : 'IPv4'}`;
};

const loadCurrentSelection = async () => {
    const { country, isp } = props.selectedDetails;
    let cities = await packageStore.getCityList(country, isp, props.selectedServerType) || [];

    if (props.selectedServerType === 'XProxy') {
        const proxySmartCities = await packageStore.getCityList(country, isp, 'ProxySmart') || [];
        const proProxyUiCities = await packageStore.getCityList(country, isp, 'ProProxyUI') || [];
        const merged = new Map();
        [...cities, ...proxySmartCities, ...proProxyUiCities].forEach((city) => {
            const key = cityStockKey(city);
            const current = merged.get(key) || { ...city, count: 0, modem_id: [] };
            merged.set(key, {
                ...current,
                count: Number(current.count || 0) + Number(city.count || 0),
                modem_id: [...new Set([...(current.modem_id || []), ...(city.modem_id || [])])],
            });
        });
        cities = [...merged.values()];
    }

    const selectedKey = cityStockKey(props.selectedDetails);
    const city = cities.find((item) => cityStockKey(item) === selectedKey);
    if (!city || Number(city.count || 0) < Number(portQuantity.value)) return null;

    const latestPlans = await packageStore.getPlans(country, isp, city.city, city.modem_id);
    const planKey = { DAY: 'daily', WEEK: 'weekly', MONTH: 'monthly' }[String(props.plan.time_unit || '').toUpperCase()];
    const plan = latestPlans?.[planKey];
    if (!plan) return null;

    return { city, plan };
};


const onUpdate = (async (values) => {
    // Enter in a field submits the form even while the button is disabled: repeat the same guard here
    if (purchaseBlocked.value) {
        const q = quote.value;
        if (q && !q.enough) useAlertStore().error(t('purchase.err.balanceShort', { balance: money(q.balance), total: money(q.total) }));
        else useAlertStore().error(t('purchase.err.checkCoupon'));
        return;
    }
    void values
    if (!loginValid(login.value, 4)) {
        useAlertStore().error(login.value.mode === 'ips' ? t('purchase.v.ipFormat') : t('purchase.err.credLength'));
        return
    }
    const { auth_user, auth_ips: auth_ip } = loginToAuth(login.value)
    // quantity arrives as text from the number input; normalise before the checks below
    const qty = Math.floor(Number(portQuantity.value));
    portQuantity.value = qty;
    const oneAuth = ((auth_user === null ? 1 : 0) ^ (auth_ip === null ? 1 : 0)) === 1;
    if (!(qty >= 1)) { useAlertStore().error(t('purchase.err.qtyMin')); return; }
    if (Number(props.maxStock) > 0 && qty > Number(props.maxStock)) { portQuantity.value = Number(props.maxStock); useAlertStore().error(t('purchase.onlyAvailable', { n: props.maxStock })); return; }
    if (!props.plan?.uuid) { useAlertStore().error(t('purchase.err.selectPackage')); return; }
    if (!props.selectedDetails?.city) { useAlertStore().error(t('purchase.err.selectCity')); return; }
    if (!oneAuth) { useAlertStore().error(t('purchase.err.chooseAuth')); return; }
    {
            buttonDisableFlag.value = true
            const currentSelection = await loadCurrentSelection();
            if (!currentSelection) {
                useAlertStore().error(t('purchase.err.stockChanged'));
                buttonDisableFlag.value = false;
                return;
            }
            if (props.preview) {
                // admin preview: the checks above ran exactly as for a customer; stop before anything is bought
                buttonDisableFlag.value = false;
                useAlertStore().success('Preview only: a customer would now pay $' + money(total.value) + ' for ' + portQuantity.value + ' x ' + (currentSelection.plan.name || 'this package') + '. Nothing was purchased.');
                return;
            }
            accountStore.purchaseByBalanceFee(currentSelection.plan.uuid, portQuantity.value, auth_user, auth_ip, currentSelection.city.city, currentSelection.city.modem_id, props.openVPN, code.value, isCoupon.value).then(async (res) => {
                buttonDisableFlag.value = false
                let modal = document.getElementsByClassName('modal-backdrop')[0];
                modal?.classList.remove('show'); // Remove the 'show' class to hide the modal
                modal?.setAttribute('aria-hidden', 'true'); // Set aria-hidden attribute to true
                modal?.setAttribute('style', 'display: none'); // Hide the modal
                let modal1 = document.getElementsByClassName('modal-backdrop')[1];
                modal1?.classList.remove('show'); // Remove the 'show' class to hide the modal
                modal1?.setAttribute('aria-hidden', 'true'); // Set aria-hidden attribute to true
                modal1?.setAttribute('style', 'display: none'); // Hide the modal
                packageStore.getCountryList(props.selectedServerType)
                if(res === 0){
                    router.push({path: '/bitcart', query: {status: -1}});
                    return
                }
                else if(res === 1) {
                    router.push({path: '/my-proxy', query: {status: 1}});
                    return
                }
                else if (res === -1) {
                    await loadCurrentSelection();
                }
            })
        }
});

watchEffect(() => {
    portQuantity.value = Math.max(0, Math.floor(portQuantity.value))
}, [portQuantity.value])
onMounted(() => modalEl.value && modalEl.value.addEventListener('show.bs.modal', resetForOpen))
onBeforeUnmount(() => modalEl.value && modalEl.value.removeEventListener('show.bs.modal', resetForOpen))
// a different plan or location also starts again from 1; a stock drop pulls the number down
watch([() => props.plan?.uuid, () => props.selectedDetails?.city, () => props.selectedDetails?.isp, () => props.selectedDetails?.country], () => { portQuantity.value = 1; stockHint.value = '' })
watch(() => props.maxStock, clampQuantity)
</script>
<style scoped>
.coupon-state { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); display: inline-flex; }
.coupon-clear { border: 0; background: transparent; padding: 0; display: inline-flex; cursor: pointer; line-height: 0; }
.coupon-clear:hover .v-icon { filter: brightness(1.25); }
.spin { animation: spin 0.9s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.preview-note { border: 1px dashed var(--ui-pri); border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; color: var(--ui-dim); }
.price-box { border: 1px solid var(--ui-line); border-radius: 8px; padding: 10px 12px; display: grid; gap: 6px; }
.price-box .row-line { display: flex; justify-content: space-between; gap: 12px; }
.price-box .row-line.total { border-top: 1px solid var(--ui-line); padding-top: 6px; font-size: 20px; font-weight: 600; }
.speed-note { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; padding: 8px 10px; border: 1px solid #f59e0b; border-radius: 8px; background: rgba(245, 158, 11, 0.12); color: var(--ui-ink); font-weight: 600; }
</style>
